import { LoyaltyOfferType } from "../../../utils/loyaltyTypes";
import { CartLoyaltyV2SliceType } from "../types/CartLoyaltyV2Types";
import {
  LoyaltyCartState,
  initialLoyaltyCartState,
} from "../types/LoyaltyCartTypes";
import { OrderCartV2Line } from "../types/OrderCartV2Types";

/** Clears active redemption session and in-flight apply UI state. */
export function clearLoyaltyRedemption(loyalty: LoyaltyCartState): void {
  loyalty.redemption = undefined;
  loyalty.applyFlow = undefined;
}

type LoyaltyRedemptionSession = NonNullable<LoyaltyCartState["redemption"]>;

/** True when user abandoned item pick/customization before any offer line was added. */
export function shouldClearRedemptionOnAbandon(
  redemption: LoyaltyRedemptionSession | undefined,
  loyaltyOfferId?: string
): boolean {
  if (!redemption?.offer) {
    return false;
  }
  if (loyaltyOfferId && redemption.offer.id !== loyaltyOfferId) {
    return false;
  }

  return (
    (redemption.addedLineIds?.length ?? 0) === 0 &&
    (redemption.pendingSelections?.length ?? 0) === 0
  );
}

type LegacyPersistedRedirectToItem =
  CartLoyaltyV2SliceType["redirectToItem"] & {
    loyaltyItemFromListOffer?: LoyaltyOfferType;
  };

type PersistedCartLoyaltyV2State = CartLoyaltyV2SliceType & {
  loyaltyCartLines?: OrderCartV2Line[];
  loyaltyCartResult?: LoyaltyCartState["result"];
  customerLoyaltyOffers?: LoyaltyOfferType[];
  redeemedLoyaltyOffer?: LoyaltyOfferType;
  redeemedOfferApplicationId?: string;
  loyaltyAddedItemLineIds?: string[];
  selectedLoyaltyOfferId?: string;
  loyaltyCartPoints?: unknown;
  redirectToItem?: LegacyPersistedRedirectToItem;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Drops persisted loyalty cart lines carrying non-uuid menu ids (numeric core
 * ids snapshotted from a pre-loyalty menu). Loyalty calculate declares
 * `idType: uuid`, so one such line 400s the whole cart ("invalid input syntax
 * for type uuid") and blocks adding items. Legacy (non-loyalty) restaurants
 * never populate this slice, so they are unaffected.
 */
function sanitizeLoyaltyCartLines(lines: OrderCartV2Line[]): OrderCartV2Line[] {
  return lines.filter(
    (line) =>
      UUID_RE.test(String(line.itemId)) &&
      UUID_RE.test(String(line.subItemId)) &&
      (line.addons ?? []).every(
        (addon) =>
          UUID_RE.test(String(addon.subItemId)) &&
          UUID_RE.test(String(addon.addonGroupId))
      )
  );
}

/**
 * One-time migration from flat V2 keys (pre–Phase 0) to nested `loyalty` sub-slice.
 */
export function migratePersistedCartLoyaltyV2State(
  state:
    | PersistedCartLoyaltyV2State
    | (Record<string, unknown> & { loyalty?: LoyaltyCartState })
): CartLoyaltyV2SliceType {
  const persisted = state as PersistedCartLoyaltyV2State;
  if (persisted.loyalty?.lines) {
    const { loyaltyCartPoints: _pts, ...rest } = persisted;
    rest.loyalty = {
      ...rest.loyalty,
      lines: sanitizeLoyaltyCartLines(rest.loyalty.lines),
    };

    return rest as CartLoyaltyV2SliceType;
  }

  const loyalty: LoyaltyCartState = {
    ...initialLoyaltyCartState,
    lines: sanitizeLoyaltyCartLines(persisted.loyaltyCartLines ?? []),
    result: persisted.loyaltyCartResult,
    customerOffers: persisted.customerLoyaltyOffers,
  };

  if (persisted.redeemedLoyaltyOffer && persisted.redeemedOfferApplicationId) {
    loyalty.redemption = {
      offer: persisted.redeemedLoyaltyOffer,
      offerApplicationId: persisted.redeemedOfferApplicationId,
      addedLineIds: persisted.loyaltyAddedItemLineIds,
    };
  }

  const loyaltyRedirect = persisted.redirectToItem?.loyaltyItemFromListOffer
    ? {
        loyaltyItemFromListOffer:
          persisted.redirectToItem.loyaltyItemFromListOffer,
      }
    : persisted.selectedLoyaltyOfferId && persisted.redirectToItem?.itemId
    ? {
        itemId: persisted.redirectToItem.itemId,
        subItemId: persisted.redirectToItem.subItemId,
      }
    : undefined;

  if (persisted.selectedLoyaltyOfferId || loyaltyRedirect) {
    loyalty.applyFlow = {
      selectedOfferId: persisted.selectedLoyaltyOfferId,
      redirect: loyaltyRedirect,
    };
  }

  const {
    loyaltyCartLines: _lines,
    loyaltyCartResult: _result,
    customerLoyaltyOffers: _offers,
    redeemedLoyaltyOffer: _redeemed,
    redeemedOfferApplicationId: _appId,
    loyaltyAddedItemLineIds: _addedIds,
    selectedLoyaltyOfferId: _selectedId,
    loyaltyCartPoints: _points,
    redirectToItem,
    ...rest
  } = persisted;

  const legacyRedirect =
    redirectToItem &&
    (redirectToItem.itemFromListOffer ||
      redirectToItem.itemId !== undefined ||
      redirectToItem.subItemId !== undefined)
      ? {
          itemId: redirectToItem.itemId,
          subItemId: redirectToItem.subItemId,
          itemFromListOffer: redirectToItem.itemFromListOffer,
        }
      : undefined;

  return {
    ...rest,
    loyalty,
    redirectToItem: legacyRedirect,
  } as CartLoyaltyV2SliceType;
}
